import * as consts from 'consts/consts';
import React, {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Button, Card, CardBody, Col } from 'reactstrap';
import { type Person } from 'types';
import { useConfirmApi, useProxyParameters } from 'utils/api/ApiHooks';
import ConfirmAPI from 'utils/api/ConfirmAPI';
import { toDateOnlyString } from 'utils/util/date';
import { useMe } from 'utils/util/hooks';
import {
  getFriendlyErrorMessageIfErrorOccurred,
  getPrettyDate,
} from 'utils/util/util';
import { suggestionGenerator } from 'views/Widgets/Dashboards/CalibrationDashboard/components/CalibrationViewList';
import EmptyState from 'views/Widgets/EmptyState';
import Loading from 'views/Widgets/Loading';
import ConfirmationDialogModal from 'views/Widgets/Modals/ConfirmationDialogModal';
import AvatarGroup from 'views/Widgets/People/AvatarGroup';
import FilterablePeopleTable, {
  FilterType,
} from 'views/Widgets/People/FilterablePeopleTable';
import CustomSurveyActionsDropdown from './CustomSurveyActionsDropdown';
import CustomSurveyEditorModal from './CustomSurveyEditorModal';
import { type CustomSurveyData, SURVEY_STATUSES, SURVEY_TYPES } from './consts';
import ManageAdministratorsModal from './ManageAdministratorsModal';

const TABLE_COLUMNS = [
  'name',
  'survey_type',
  'status',
  'question_count',
  'instance_count',
  'owner',
  'administrators',
  'created_at',
  'actions',
] as const;

const CustomSurveysTab: FC = () => {
  const { formatMessage, locale } = useIntl();
  const history = useHistory();
  const proxyParameters = useProxyParameters();
  const me = useMe();

  const [surveys, setSurveys] = useState<CustomSurveyData[] | undefined>(
    undefined
  );
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [editingSurvey, setEditingSurvey] = useState<CustomSurveyData | null>(
    null
  );
  // editingSurvey (state) drives rendering. editingSurveyRef mirrors it so
  // that handleEditorSave — which fires asynchronously after the modal's
  // close animation clears state — always sees the survey that was active.
  const editingSurveyRef = useRef<CustomSurveyData | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deletingSurvey, setDeletingSurvey] = useState<CustomSurveyData | null>(
    null
  );
  const [isManageAdminsModalOpen, setIsManageAdminsModalOpen] = useState(false);
  const [managingAdminsSurvey, setManagingAdminsSurvey] =
    useState<CustomSurveyData | null>(null);

  const callback = useCallback(
    (
      data: CustomSurveyData[] | null,
      error: unknown,
      hardError: unknown
    ): void => {
      if (!data || error || hardError) {
        return;
      }
      setSurveys(data);
    },
    []
  );

  const { status } = useConfirmApi<CustomSurveyData[]>({
    method: 'GET',
    url: 'custom-survey/surveys',
    callback,
  });

  // Clear editingSurvey when modal closes so stale data doesn't leak
  useEffect(() => {
    if (!isEditorOpen) {
      setEditingSurvey(null);
    }
  }, [isEditorOpen]);

  const handleCreateClick = useCallback(() => {
    editingSurveyRef.current = null;
    setEditingSurvey(null);
    setIsEditorOpen(true);
  }, []);

  const handleEditClick = useCallback(
    (surveyId: number) => {
      history.push(
        consts
          .ADMINISTRATION_CUSTOM_SURVEYS_EDITOR(formatMessage)
          .path.replace(':id', String(surveyId))
      );
    },
    [history, formatMessage]
  );

  const handleViewResponsesClick = useCallback(
    (surveyId: number) => {
      history.push(
        consts
          .ADMINISTRATION_CUSTOM_SURVEYS_RESPONSES(formatMessage)
          .path.replace(':id', String(surveyId))
      );
    },
    [history, formatMessage]
  );

  const toggleEditor = useCallback(() => {
    setIsEditorOpen((prev) => !prev);
  }, []);

  const handleManageAdministratorsClick = useCallback(
    (surveyId: number) => {
      const survey = surveys?.find((s) => s.id === surveyId);
      if (survey) {
        setManagingAdminsSurvey(survey);
        setIsManageAdminsModalOpen(true);
      }
    },
    [surveys]
  );

  const toggleManageAdminsModal = useCallback(() => {
    setIsManageAdminsModalOpen((prev) => !prev);
  }, []);

  const handleManageAdministratorsSave = useCallback(
    (data: Partial<CustomSurveyData>) => {
      if (!managingAdminsSurvey?.id) {
        return;
      }

      ConfirmAPI.sendRequestToConfirm(
        'PATCH',
        `custom-survey/surveys/${managingAdminsSurvey.id}`,
        { ...proxyParameters, administrator_ids: data.administrator_ids },
        (responseData, error, hardErrorMessage) => {
          if (error || hardErrorMessage) {
            toast.error(
              formatMessage({
                id: 'app.views.administration.custom_surveys.surveys.manage_administrators.save_error',
                defaultMessage: 'Failed to update administrators.',
              })
            );
            return;
          }
          setSurveys((prev) =>
            prev?.map((s) => (s.id === responseData.id ? responseData : s))
          );
          setIsManageAdminsModalOpen(false);
        }
      );
    },
    [managingAdminsSurvey, proxyParameters, formatMessage]
  );

  // Clear managingAdminsSurvey when modal closes so stale data doesn't leak
  useEffect(() => {
    if (!isManageAdminsModalOpen) {
      setManagingAdminsSurvey(null);
    }
  }, [isManageAdminsModalOpen]);

  const handleEditorSave = useCallback(
    (data: Partial<CustomSurveyData>) => {
      const editing = editingSurveyRef.current;
      const isEditing = !!editing?.id;

      if (isEditing) {
        ConfirmAPI.sendRequestToConfirm(
          'PATCH',
          `custom-survey/surveys/${editing.id}`,
          { ...proxyParameters, ...data },
          (responseData, error, hardErrorMessage) => {
            if (error || hardErrorMessage) {
              // Backend field errors (e.g. a queued person who no longer
              // matches the audience) carry the actual reason - without this
              // they were all flattened to the generic message below.
              const backendMessage = getFriendlyErrorMessageIfErrorOccurred(
                error,
                hardErrorMessage
              );
              toast.error(
                backendMessage ??
                  formatMessage({
                    id: 'app.views.administration.custom_surveys.surveys.save_error',
                    defaultMessage: 'Failed to save survey.',
                  })
              );
              return;
            }
            setSurveys((prev) =>
              prev?.map((s) => (s.id === responseData.id ? responseData : s))
            );
            setIsEditorOpen(false);
          }
        );
      } else {
        ConfirmAPI.sendRequestToConfirm(
          'POST',
          'custom-survey/surveys',
          { ...proxyParameters, ...data },
          (responseData, error, hardErrorMessage) => {
            if (error || hardErrorMessage) {
              const backendMessage = getFriendlyErrorMessageIfErrorOccurred(
                error,
                hardErrorMessage
              );
              toast.error(
                backendMessage ??
                  formatMessage({
                    id: 'app.views.administration.custom_surveys.surveys.create_error',
                    defaultMessage: 'Failed to create survey.',
                  })
              );
              return;
            }
            setSurveys((prev) =>
              prev ? [responseData, ...prev] : [responseData]
            );
            setIsEditorOpen(false);
            // Redirect to full-page editor to add questions
            if (responseData?.id) {
              history.push(
                consts
                  .ADMINISTRATION_CUSTOM_SURVEYS_EDITOR(formatMessage)
                  .path.replace(':id', String(responseData.id))
              );
            }
          }
        );
      }
    },
    [proxyParameters, formatMessage, history]
  );

  const handleDeleteClick = useCallback(
    (surveyId: number) => {
      const survey = surveys?.find((s) => s.id === surveyId);
      if (survey) {
        setDeletingSurvey(survey);
        setIsDeleteModalOpen(true);
      }
    },
    [surveys]
  );

  const handleDeleteConfirm = useCallback(() => {
    if (!deletingSurvey?.id) return;

    ConfirmAPI.sendRequestToConfirm(
      'DELETE',
      `custom-survey/surveys/${deletingSurvey.id}`,
      proxyParameters,
      (_data, error, hardErrorMessage) => {
        if (error || hardErrorMessage) {
          toast.error(
            formatMessage({
              id: 'app.views.administration.custom_surveys.surveys.delete_error',
              defaultMessage: 'Failed to delete survey.',
            })
          );
          return;
        }
        setSurveys((prev) => prev?.filter((s) => s.id !== deletingSurvey.id));
        setIsDeleteModalOpen(false);
        setDeletingSurvey(null);
      }
    );
  }, [deletingSurvey, proxyParameters, formatMessage]);

  const handleCloseDeleteModal = useCallback(() => {
    setIsDeleteModalOpen(false);
    setDeletingSurvey(null);
  }, []);

  const handleStatusChange = useCallback(
    (surveyId: number, newStatus: string, errorMsg: string) => {
      ConfirmAPI.sendRequestToConfirm(
        'PATCH',
        `custom-survey/surveys/${surveyId}`,
        { ...proxyParameters, status: newStatus },
        (data, error, hardErrorMessage) => {
          if (error || hardErrorMessage) {
            const detail = error?.data?.detail;
            const backendMsg = Array.isArray(detail) ? detail[0] : detail;
            toast.error(typeof backendMsg === 'string' ? backendMsg : errorMsg);
            return;
          }
          setSurveys((prev) => prev?.map((s) => (s.id === data.id ? data : s)));
        }
      );
    },
    [proxyParameters]
  );

  const handleArchiveClick = useCallback(
    (surveyId: number) =>
      handleStatusChange(
        surveyId,
        'ARCHIVED',
        formatMessage({
          id: 'app.views.administration.custom_surveys.surveys.archive_error',
          defaultMessage: 'Failed to archive survey.',
        })
      ),
    [handleStatusChange, formatMessage]
  );

  const handleSetLiveClick = useCallback(
    (surveyId: number) =>
      handleStatusChange(
        surveyId,
        'LIVE',
        formatMessage({
          id: 'app.views.administration.custom_surveys.surveys.set_live_error',
          defaultMessage: 'Failed to set survey live.',
        })
      ),
    [handleStatusChange, formatMessage]
  );

  const surveyTypes = useMemo(
    () => SURVEY_TYPES(formatMessage),
    [formatMessage]
  );
  const surveyStatuses = useMemo(
    () => SURVEY_STATUSES(formatMessage),
    [formatMessage]
  );

  const COLUMN_DEFS = useMemo(
    () =>
      ({
        name: {
          name: formatMessage({
            id: 'app.views.administration.custom_surveys.surveys.table.name',
            defaultMessage: 'Name',
          }),
          field: 'name',
          format: (name?: string) => (
            <span className="text-wrap" style={{ whiteSpace: 'normal' }}>
              {name || '-'}
            </span>
          ),
        },
        survey_type: {
          name: formatMessage({
            id: 'app.views.administration.custom_surveys.surveys.table.type',
            defaultMessage: 'Type',
          }),
          field: 'survey_type',
        },
        status: {
          name: formatMessage({
            id: 'app.views.administration.custom_surveys.surveys.table.status',
            defaultMessage: 'Status',
          }),
          field: 'status',
          className: 'col-1',
        },
        question_count: {
          name: formatMessage({
            id: 'app.views.administration.custom_surveys.surveys.table.questions',
            defaultMessage: 'Questions',
          }),
          field: 'question_count',
          className: 'col-1',
          hideFromFilters: true,
        },
        instance_count: {
          name: formatMessage({
            id: 'app.views.administration.custom_surveys.surveys.table.instances',
            defaultMessage: 'Instances',
          }),
          field: 'instance_count',
          className: 'col-1',
          hideFromFilters: true,
        },
        owner: {
          name: formatMessage({
            id: 'app.views.administration.custom_surveys.surveys.table.owner',
            defaultMessage: 'Owner',
          }),
          field: 'owner',
          className: 'col-2',
          isPerson: true,
          getFilterDisplayValue: (person?: Person | null) =>
            person?.full_name ?? '',
          nameTransformerFunction: (name: string) =>
            formatMessage(
              {
                id: 'app.views.administration.custom_surveys.surveys.table.owner.filter',
                defaultMessage: '{name} as owner',
              },
              { name }
            ),
          format: (person?: Person | null) =>
            person ? <AvatarGroup people={[person]} maxFaces={1} /> : '-',
        },
        administrators: {
          name: formatMessage({
            id: 'app.views.administration.custom_surveys.surveys.table.administrators',
            defaultMessage: 'Administrators',
          }),
          field: 'administrators',
          className: 'col-2',
          sortable: false,
          isPerson: true,
          getFilterDisplayValue: (person?: Person | null) =>
            person?.full_name ?? '',
          nameTransformerFunction: (name: string) =>
            formatMessage(
              {
                id: 'app.views.administration.custom_surveys.surveys.table.administrators.filter',
                defaultMessage: '{name} as administrator',
              },
              { name }
            ),
          fieldTransformer: (value, operator) =>
            `(${operator == 'EQ' ? 'in' : 'not-in'} "${
              value.filterObject.value
            }" administrator_ids)`,
          format: (admins?: Person[]) =>
            admins?.length ? <AvatarGroup people={admins} maxFaces={2} /> : '-',
        },
        created_at: {
          name: formatMessage({
            id: 'app.views.administration.custom_surveys.surveys.table.created',
            defaultMessage: 'Created',
          }),
          field: 'created_at',
          className: 'col-2',
          format: (date: string) =>
            getPrettyDate({ dateString: date, includeTime: false, locale }),
          getFilterDisplayValue: (date: string) =>
            getPrettyDate({ dateString: date, includeTime: false, locale }),
        },
        actions: {
          name: formatMessage({
            id: 'app.views.administration.custom_surveys.surveys.table.actions',
            defaultMessage: 'Actions',
          }),
          field: 'actions',
          className: 'text-end text-nowrap',
          sortable: false,
          hideFromFilters: true,
        },
      }) as const,
    [formatMessage, locale]
  );

  const columns = useMemo(
    () => TABLE_COLUMNS.map((key) => COLUMN_DEFS[key]),
    [COLUMN_DEFS]
  );

  const toRow = useCallback(
    (s: CustomSurveyData, isOwnerOrAdmin: boolean) => ({
      id: s.id,
      name: s.name,
      survey_type:
        surveyTypes.find((t) => t.id === s.survey_type)?.name ??
        s.survey_type ??
        '-',
      status: surveyStatuses.find((st) => st.id === s.status)?.name ?? s.status,
      question_count: s.questions_at_latest_version?.length ?? 0,
      instance_count: s.instance_count ?? 0,
      owner: s.owner,
      administrators: s.administrators ?? [],
      administrator_ids: (s.administrators ?? []).map((a) => a.id),
      created_at: toDateOnlyString(s.created_at),
      actions: (
        <div className="d-flex align-items-center justify-content-end gap-2">
          <CustomSurveyActionsDropdown
            surveyId={s.id!}
            isOwnerOrAdmin={isOwnerOrAdmin}
            status={s.status}
            surveyType={s.survey_type}
            hasQuestions={(s.questions_at_latest_version?.length ?? 0) > 0}
            hasInstances={(s.instance_count ?? 0) > 0}
            hasResponses={s.has_responses ?? false}
            onEditClick={handleEditClick}
            onViewResponsesClick={handleViewResponsesClick}
            onManageAdministratorsClick={handleManageAdministratorsClick}
            onArchiveClick={handleArchiveClick}
            onSetLiveClick={handleSetLiveClick}
            onDeleteClick={handleDeleteClick}
          />
        </div>
      ),
    }),
    [
      handleEditClick,
      handleViewResponsesClick,
      handleManageAdministratorsClick,
      handleArchiveClick,
      handleSetLiveClick,
      handleDeleteClick,
      surveyTypes,
      surveyStatuses,
    ]
  );

  const isOwnerOrAdminOf = useCallback(
    (s: CustomSurveyData) =>
      me?.id === s.owner?.id ||
      (s.administrators ?? []).some((a) => a.id === me?.id),
    [me]
  );

  // Sort order is computed once from the initial fetch and preserved across
  // status changes so that rows don't jump around when archiving / restoring.
  const sortOrderRef = useRef<number[]>([]);

  const rows = useMemo(() => {
    if (!surveys) return [];

    const hasOrder = sortOrderRef.current.length > 0;
    if (!hasOrder) {
      const sorted = [...surveys].sort((a, b) => {
        const aIsOwn = isOwnerOrAdminOf(a);
        const bIsOwn = isOwnerOrAdminOf(b);
        if (aIsOwn !== bIsOwn) return aIsOwn ? -1 : 1;
        // DRAFT/LIVE before ARCHIVED
        const aActive = a.status === 'DRAFT' || a.status === 'LIVE';
        const bActive = b.status === 'DRAFT' || b.status === 'LIVE';
        if (aActive !== bActive) return aActive ? -1 : 1;
        return (a.name ?? '').localeCompare(b.name ?? '', locale);
      });
      sortOrderRef.current = sorted.map((s) => s.id!);
      return sorted.map((s) => toRow(s, isOwnerOrAdminOf(s)));
    }

    const byId = new Map(surveys.map((s) => [s.id!, s]));
    const ordered: CustomSurveyData[] = [];
    const seen = new Set<number>();

    for (const s of surveys) {
      if (!sortOrderRef.current.includes(s.id!)) {
        ordered.push(s);
        seen.add(s.id!);
      }
    }
    for (const id of sortOrderRef.current) {
      const s = byId.get(id);
      if (s && !seen.has(id)) {
        ordered.push(s);
      }
    }
    sortOrderRef.current = ordered.map((s) => s.id!);
    return ordered.map((s) => toRow(s, isOwnerOrAdminOf(s)));
  }, [surveys, isOwnerOrAdminOf, toRow, locale]);

  if (status === 'LOADING') {
    return (
      <Loading
        message={formatMessage({
          id: 'app.views.administration.custom_surveys.surveys.loading',
          defaultMessage: 'Loading surveys...',
        })}
      />
    );
  }

  const hasNoSurveys = !surveys?.length;

  return (
    <div className="container-fluid">
      {hasNoSurveys ? (
        <Card>
          <CardBody>
            <EmptyState
              title={formatMessage({
                id: 'app.views.administration.custom_surveys.surveys.empty.title',
                defaultMessage: 'No surveys yet',
              })}
              subtitle={formatMessage({
                id: 'app.views.administration.custom_surveys.surveys.empty.subtitle',
                defaultMessage:
                  'Create lifecycle surveys to gather feedback from your team.',
              })}
            >
              <Button color="primary" onClick={handleCreateClick}>
                <FormattedMessage
                  id="app.views.administration.custom_surveys.surveys.empty.create_button"
                  defaultMessage="Create your first survey"
                />
              </Button>
            </EmptyState>
          </CardBody>
        </Card>
      ) : (
        <FilterablePeopleTable
          columns={columns}
          rows={rows}
          filterType={FilterType.ADVANCED}
          suggestionGenerator={suggestionGenerator}
          hideExportButton={true}
          actions={
            <Col className="col-auto ps-0 ms-auto d-flex flex-wrap gap-2 align-items-center">
              <Button color="primary" onClick={handleCreateClick}>
                <FormattedMessage
                  id="app.views.administration.custom_surveys.surveys.create_button"
                  defaultMessage="Create survey"
                />
              </Button>
            </Col>
          }
        />
      )}

      <CustomSurveyEditorModal
        isOpen={isEditorOpen}
        toggle={toggleEditor}
        onSave={handleEditorSave}
        existingSurvey={editingSurvey}
      />

      <ManageAdministratorsModal
        isOpen={isManageAdminsModalOpen}
        toggle={toggleManageAdminsModal}
        onSave={handleManageAdministratorsSave}
        entity={managingAdminsSurvey}
      />

      {isDeleteModalOpen && deletingSurvey && (
        <ConfirmationDialogModal
          isOpen={isDeleteModalOpen}
          onClosed={handleCloseDeleteModal}
          toggle={handleCloseDeleteModal}
          confirmCallback={handleDeleteConfirm}
          title={formatMessage({
            id: 'app.views.administration.custom_surveys.surveys.delete.title',
            defaultMessage: 'Delete survey',
          })}
          description={formatMessage({
            id: 'app.views.administration.custom_surveys.surveys.delete.description',
            defaultMessage:
              'Are you sure you want to delete this survey? This cannot be undone.',
          })}
          confirmText={formatMessage({
            id: 'app.views.administration.custom_surveys.surveys.delete.confirm',
            defaultMessage: 'Delete',
          })}
        />
      )}
    </div>
  );
};

export default React.memo(CustomSurveysTab);
